import { useMemo, useState } from "react";
import {
  Column,
  useBlockLayout,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useSticky } from "react-table-sticky";

import {
  VoutboundPending,
  VoutboundPendingData,
  TaskStatusRequest,
} from "../../../types/api";
import Text from "../Text";
import PaginationV2 from "../Pagination";
import { COLORS } from "../../../styles/colors";
import { FONT_WEIGHT } from "../../../styles/fonts";

const isHighlight = (row: VoutboundPending): boolean =>
  (row.elapsedSec !== null && row.elapsedSec > 60) ||
  row.umProcessHkt === null ||
  row.cxolaInProcessing === 1;

const VoutboundBox = ({
  searchResults,
  totalRecords,
  limit,
  fetchRefreshResults,
  alertColor,
}: {
  searchResults: VoutboundPendingData;
  totalRecords: number;
  limit: number;
  fetchRefreshResults: (request: TaskStatusRequest) => void;
  alertColor?: boolean;
}): JSX.Element => {
  const COLUMNS = useMemo<Column<VoutboundPending>[]>(
    () => [
      {
        accessor: "progressRecId",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            PROGRESS_RECID
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "10%",
      },
      {
        accessor: "umMsgStat",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            UM_MSG_STAT
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "8%",
      },
      {
        accessor: "trxNo",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            TRX_NO
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "12%",
      },
      {
        accessor: "umReceiveHkt",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            UM_RECEIVE_HKT
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "14%",
      },
      {
        accessor: "umProcessHkt",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            UM_PROCESS_HKT
          </span>
        ),
        Cell: ({ value }) => <Text>{value ?? "--"}</Text>,
        width: "14%",
      },
      {
        accessor: "cxolaRecordHkt",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            CXOLA_RECORD_HKT
          </span>
        ),
        Cell: ({ value }) => <Text>{value ?? "--"}</Text>,
        width: "14%",
      },
      {
        accessor: "elapsedSec",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            ELAPSED_SEC
          </span>
        ),
        Cell: ({ value }) => (
          <Text>
            {value !== null && value !== undefined ? String(value) : "--"}
          </Text>
        ),
        width: "8%",
      },
      {
        accessor: "cxolaSent",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            CXOLA_SENT
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "10%",
      },
      {
        accessor: "cxolaInProcessing",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            CXOLA_IN_PROCESSING
          </span>
        ),
        Cell: ({ value }) => (
          <Text>
            {value !== null && value !== undefined ? String(value) : "--"}
          </Text>
        ),
        width: "10%",
      },
    ],
    [],
  );

  const { page, headerGroups, getTableProps, getTableBodyProps, prepareRow } =
    useTable(
      {
        columns: COLUMNS,
        data: searchResults,
      },
      useSortBy,
      usePagination,
      useSticky,
      useBlockLayout,
    );

  const [pageIndex, setPageIndex] = useState(0);

  const gotoPage = (updater: number) => {
    setPageIndex(updater);
    fetchRefreshResults({ limit, pendingOffset: updater * limit });
  };

  const nextPage = () => {
    if (pageIndex < Math.ceil(totalRecords / limit) - 1) {
      setPageIndex((prev) => prev + 1);
      fetchRefreshResults({ limit, pendingOffset: (pageIndex + 1) * limit });
    }
  };

  const previousPage = () => {
    if (pageIndex > 0) {
      setPageIndex((prev) => prev - 1);
      fetchRefreshResults({ limit, pendingOffset: (pageIndex - 1) * limit });
    }
  };

  return (
    <div style={{ padding: "0 1rem" }}>
      {totalRecords > 0 ? (
        <>
          <table
            {...getTableProps()}
            style={{
              borderRadius: "0.625rem",
              fontSize: "2rem",
              borderTop: `0.0625rem solid ${COLORS.grey5}`,
              borderRight: `0.0625rem solid ${COLORS.grey5}`,
              borderLeft: `0.0625rem solid ${COLORS.grey5}`,
              borderCollapse: "collapse",
              border: "0",
              borderSpacing: "0",
              cursor: "default",
              width: "100%",
            }}
          >
            <thead
              style={{
                position: "relative",
                top: 0,
                width: "100%",
                zIndex: 5,
              }}
            >
              {headerGroups.map((headerGroup) => {
                const { key, ...restHeaderGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr
                    key={key}
                    {...restHeaderGroupProps}
                    style={{ height: "3.75rem", lineHeight: "3.125rem" }}
                  >
                    {headerGroup.headers.map((column) => {
                      const { key: thKey, ...restThProps } =
                        column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th
                          key={thKey}
                          {...restThProps}
                          style={{
                            width: column.width,
                            letterSpacing: "0.019rem",
                            paddingLeft: "0.5rem",
                            color: COLORS.black,
                            fontSize: "0.875rem",
                            fontWeight: FONT_WEIGHT.regular,
                            padding: "0.3125rem",
                            overflow: "hidden",
                          }}
                        >
                          {column.render("Header")}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()} id="tableBody">
              {page.map((row) => {
                prepareRow(row);
                const { key, ...restRowProps } = row.getRowProps();
                const highlight = alertColor && isHighlight(row.original);
                return (
                  <tr
                    key={key}
                    id="tableRow"
                    {...restRowProps}
                    style={{
                      fontSize: "0.875rem",
                      display: "inline-block !important",
                      width: "100%",
                      letterSpacing: "0.019rem",
                      borderBottom: `0.0625rem solid ${COLORS.grey10}`,
                    }}
                  >
                    {row.cells.map((cell) => {
                      const { key: tdKey, ...restCellProps } =
                        cell.getCellProps();
                      return (
                        <td
                          className={highlight ? "yellow" : ""}
                          key={tdKey}
                          id="tableData"
                          {...restCellProps}
                          style={{
                            overflowWrap: "break-word",
                            padding: "0.5rem 0.4rem 0.5rem 0.5rem",
                            letterSpacing: "0.019rem",
                            width: cell.column.width,
                            minWidth: cell.column.minWidth,
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationV2
            gotoPage={gotoPage}
            nextPage={nextPage}
            previousPage={previousPage}
            pageIndex={pageIndex}
            noOfPages={Math.ceil(totalRecords / limit)}
          />
        </>
      ) : null}
    </div>
  );
};

export default VoutboundBox;
